import React, { useEffect, useMemo, useState } from "react";
import { schoolLeaderboard } from "../../lib/aggregateAnalytics";
import { fetchLeanAnalytics } from "../../lib/fetchRegistrationAnalytics";
import { loadAnalyticsConfig } from "../../lib/schoolAliasesFirestore";
import {
  buildAliasLookup,
  applySchoolAliases,
  filterHiddenRecords,
} from "../../lib/schoolAliases";
import { buildOutreachRows } from "../../lib/schoolOutreach";
import { loadOutreachTracking } from "../../lib/outreachTrackingFirestore";
import {
  summarizeQuizBySchool,
  enrichRowQuizMeta,
} from "../../lib/quizSeason";
import {
  getOutreachMemberList,
  MAX_SCHOOLS_PER_USER,
} from "../../lib/outreachMvpCredentials";
import {
  computeOutreachMemberStats,
  computeTeamTotals,
  performanceTier,
} from "../../lib/outreachMemberStats";
import { CALL_STATUS_OPTIONS } from "../../lib/outreachTrackingFirestore";

function ProgressBar({ value, max = 100, className = "" }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={`outreach-perf-bar-wrap ${className}`}>
      <div className="outreach-perf-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

function callLabel(id) {
  return CALL_STATUS_OPTIONS.find((o) => o.id === id)?.label ?? id;
}

function MemberSchoolsDetail({ schools }) {
  if (schools.length === 0) {
    return <p className="outreach-admin-empty-schools">No schools claimed yet.</p>;
  }
  return (
    <div className="outreach-admin-schools-wrap">
      <table className="analytics-table outreach-admin-schools-table">
        <thead>
          <tr>
            <th>School</th>
            <th>Regs</th>
            <th>Quiz</th>
            <th>ICT</th>
            <th>Call</th>
            <th>Msg</th>
            <th>Contact</th>
            <th>Comments</th>
          </tr>
        </thead>
        <tbody>
          {schools.map((s) => (
            <tr key={s.key}>
              <td>{s.displayName}</td>
              <td>{s.regCount}</td>
              <td>
                {s.quizTeams > 0 ? (
                  <span className="quiz-badge">
                    {s.quizTeams}/2{s.quizTeams >= 2 ? " ✓" : ""}
                  </span>
                ) : (
                  <span className="lb-none">—</span>
                )}
              </td>
              <td>{s.hasIct ? <span className="ict-badge">✓</span> : "—"}</td>
              <td>{callLabel(s.callStatus)}</td>
              <td>{s.messageSent ? "✓" : "—"}</td>
              <td>
                {s.contactPhone ? (
                  <span title={s.contactPhone} style={{ fontSize: "11px" }}>
                    {s.contactPhone} <strong style={{ color: "#ff8080" }}>({s.contactRole || "TIC"})</strong>
                  </span>
                ) : (
                  <span className="lb-none">—</span>
                )}
              </td>
              <td style={{ maxWidth: "200px" }}>
                {s.notes ? (
                  <span title={s.notes} style={{ fontSize: "11px", wordBreak: "break-word" }}>
                    {s.notes.length > 50 ? `${s.notes.slice(0, 47)}...` : s.notes}
                  </span>
                ) : (
                  <span className="lb-none">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OutreachAdminPanel() {
  const [loading, setLoading] = useState(true);
  const [memberStats, setMemberStats] = useState([]);
  const [expandedEmail, setExpandedEmail] = useState("");
  const [sortBy, setSortBy] = useState("performance");
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ records }, config, tracking] = await Promise.all([
          fetchLeanAnalytics(),
          loadAnalyticsConfig(),
          loadOutreachTracking(),
        ]);
        const lookup = buildAliasLookup(config.groups);
        const visible = filterHiddenRecords(
          applySchoolAliases(records, lookup),
          config.hiddenSchools
        );
        const baseRows = schoolLeaderboard(visible);
        const { current, stale } = summarizeQuizBySchool(visible);
        const ictKeys = new Set();
        for (const r of visible) {
          if (r.collection !== "ict_society_registrations") continue;
          const k =
            (r.schoolKey ?? (r.schoolName ?? "").trim().toLowerCase()) ||
            "(unknown)";
          ictKeys.add(k);
        }
        const rowsWithMeta = baseRows.map((row) => ({
          ...row,
          ...enrichRowQuizMeta(row.key, tracking.schoolState, current, stale),
          hasIct: ictKeys.has(row.key),
        }));
        const schools = buildOutreachRows(
          rowsWithMeta,
          config.manualSchools,
          config.schoolContacts
        );
        const stats = computeOutreachMemberStats(
          tracking.schoolState,
          schools,
          getOutreachMemberList()
        );
        setMemberStats(stats);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = useMemo(() => computeTeamTotals(memberStats), [memberStats]);

  const sortedMembers = useMemo(() => {
    let list = [...memberStats];
    if (!showInactive) {
      list = list.filter((m) => m.claimed > 0 || m.quizConfirmationsDone > 0);
    }
    const cmp = {
      performance: (a, b) => b.performanceScore - a.performanceScore,
      claimed: (a, b) => b.claimed - a.claimed,
      quiz: (a, b) => b.quizFull - a.quizFull,
      messaged: (a, b) => b.messaged - a.messaged,
      name: (a, b) => a.name.localeCompare(b.name),
    };
    const fn = cmp[sortBy] ?? cmp.performance;
    list.sort(fn);
    return list;
  }, [memberStats, sortBy, showInactive]);

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="analytics-spinner" />
        <p>Loading team performance…</p>
      </div>
    );
  }

  return (
    <div className="outreach-admin-panel">
      <div className="outreach-admin-intro">
        <h2>Team performance</h2>
        <p>
          Outreach member activity — schools claimed, calls, messages, and quiz
          outcomes on each member&apos;s portfolio.
        </p>
      </div>

      <div className="outreach-admin-summary">
        <div className="outreach-admin-summary-card">
          <span className="outreach-admin-summary-val">{totals.memberCount}</span>
          <span className="outreach-admin-summary-label">Members</span>
        </div>
        <div className="outreach-admin-summary-card">
          <span className="outreach-admin-summary-val">{totals.activeMembers}</span>
          <span className="outreach-admin-summary-label">Active</span>
        </div>
        <div className="outreach-admin-summary-card">
          <span className="outreach-admin-summary-val">{totals.totalClaimed}</span>
          <span className="outreach-admin-summary-label">Schools claimed</span>
        </div>
        <div className="outreach-admin-summary-card">
          <span className="outreach-admin-summary-val">{totals.totalCalled}</span>
          <span className="outreach-admin-summary-label">Calls made</span>
        </div>
        <div className="outreach-admin-summary-card">
          <span className="outreach-admin-summary-val">{totals.totalMessaged}</span>
          <span className="outreach-admin-summary-label">Messages sent</span>
        </div>
        <div className="outreach-admin-summary-card highlight">
          <span className="outreach-admin-summary-val">{totals.avgPerformance}</span>
          <span className="outreach-admin-summary-label">Avg score</span>
        </div>
      </div>

      <div className="outreach-toolbar outreach-admin-toolbar">
        <div className="outreach-toolbar-row">
          <select
            className="analytics-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="performance">Sort: Performance</option>
            <option value="claimed">Sort: Schools claimed</option>
            <option value="quiz">Sort: Quiz complete (2/2)</option>
            <option value="messaged">Sort: Messages sent</option>
            <option value="name">Sort: Name</option>
          </select>
          <label className="outreach-check-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show members with no claims
          </label>
        </div>
      </div>

      <div className="leaderboard-table-wrapper outreach-admin-table-wrap">
        <table className="analytics-table outreach-admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Member</th>
              <th>Score</th>
              <th>Claimed</th>
              <th>Called</th>
              <th>Answered</th>
              <th>Messaged</th>
              <th>Quiz 2/2</th>
              <th>Quiz ⚠</th>
              <th>New quiz</th>
              <th>ICT</th>
              <th>Regs</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.map((m, idx) => {
              const tier = performanceTier(m.performanceScore);
              const expanded = expandedEmail === m.email;
              return (
                <React.Fragment key={m.email}>
                  <tr
                    className={[
                      m.isAdmin ? "outreach-admin-row-admin" : "",
                      expanded ? "outreach-admin-row-expanded" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <td className="rank-cell">{idx + 1}</td>
                    <td className="school-name-cell">
                      {m.name}
                      {m.isAdmin && (
                        <span className="outreach-admin-tag">Admin</span>
                      )}
                      <span className="outreach-reg-count">{m.email}</span>
                    </td>
                    <td>
                      <span className={`outreach-perf-score ${tier.className}`}>
                        {m.performanceScore}
                      </span>
                      <span className={`outreach-perf-tier ${tier.className}`}>
                        {tier.label}
                      </span>
                      <ProgressBar value={m.performanceScore} />
                    </td>
                    <td>
                      <strong>{m.claimed}</strong>
                      <span className="outreach-admin-mini-hint">
                        /{MAX_SCHOOLS_PER_USER}
                      </span>
                    </td>
                    <td>
                      {m.called}
                      {m.claimed > 0 && (
                        <span className="outreach-admin-pct">{m.callRate}%</span>
                      )}
                    </td>
                    <td>{m.answered}</td>
                    <td>
                      {m.messaged}
                      {m.claimed > 0 && (
                        <span className="outreach-admin-pct">{m.messageRate}%</span>
                      )}
                    </td>
                    <td>
                      {m.quizFull}
                      {m.claimed > 0 && (
                        <span className="outreach-admin-pct">
                          {m.quizCompleteRate}%
                        </span>
                      )}
                    </td>
                    <td>
                      {m.quizPending > 0 ? (
                        <span className="outreach-admin-warn">{m.quizPending}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{m.newQuizTeams || "—"}</td>
                    <td>{m.ictRegistered || "—"}</td>
                    <td>{m.totalRegs || "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="outreach-admin-expand-btn"
                        onClick={() =>
                          setExpandedEmail(expanded ? "" : m.email)
                        }
                      >
                        {expanded ? "Hide" : `${m.claimed} schools`}
                      </button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="outreach-admin-detail-row">
                      <td colSpan={13}>
                        <MemberSchoolsDetail schools={m.schools} />
                        {m.quizConfirmationsDone > 0 && (
                          <p className="outreach-admin-extra-stat">
                            Quiz confirmations recorded:{" "}
                            <strong>{m.quizConfirmationsDone}</strong>
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="outreach-admin-cards">
        {sortedMembers.map((m, idx) => {
          const tier = performanceTier(m.performanceScore);
          const expanded = expandedEmail === m.email;
          return (
            <div key={m.email} className="outreach-admin-member-card">
              <div className="outreach-admin-card-head">
                <span className="lb-card-rank">#{idx + 1}</span>
                <span className="lb-card-name">{m.name}</span>
                <span className={`outreach-perf-score ${tier.className}`}>
                  {m.performanceScore}
                </span>
              </div>
              <div className="outreach-admin-card-stats">
                <span>{m.claimed}/{MAX_SCHOOLS_PER_USER} claimed</span>
                <span>{m.called} called</span>
                <span>{m.messaged} messaged</span>
                <span>{m.quizFull} quiz 2/2</span>
              </div>
              <ProgressBar value={m.performanceScore} className="outreach-perf-bar-card" />
              <button
                type="button"
                className="outreach-admin-expand-btn"
                onClick={() => setExpandedEmail(expanded ? "" : m.email)}
              >
                {expanded ? "Hide schools" : "View schools"}
              </button>
              {expanded && <MemberSchoolsDetail schools={m.schools} />}
            </div>
          );
        })}
      </div>

      <p className="outreach-admin-footnote">
        Performance score weighs claim usage, call &amp; message rates, quiz
        completion on assigned schools, and quiz confirmations resolved.
      </p>
    </div>
  );
}
